import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { AlertTriangle, CheckCircle2, Play, SkipForward, Zap } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What does MATCH return?',
    options: [
      'The value in the matching cell',
      'The position (row number) of the matching value within the range',
      'TRUE or FALSE depending on whether a match was found',
      'The address of the matching cell (e.g., A3)',
    ],
    correctIndex: 1,
    explanation:
      'MATCH returns the relative position (a number) of the matching value within the lookup range. For example, if the match is the 3rd item in the range, MATCH returns 3.',
  },
  {
    question: 'What does INDEX return?',
    options: [
      'The position of a value',
      'TRUE if the value exists',
      'The value at a specific position in a range',
      'The cell address',
    ],
    correctIndex: 2,
    explanation:
      'INDEX returns the actual value at a given position in a range. INDEX(B2:B10, 3) returns whatever is in the 3rd cell of the range B2:B10.',
  },
  {
    question: 'In INDEX/MATCH, which part finds WHERE the data is?',
    options: ['INDEX', 'MATCH', 'Both equally', 'Neither — it uses cell references'],
    correctIndex: 1,
    explanation:
      'MATCH is the "finder" — it locates the position of the search key. INDEX is the "fetcher" — it uses that position to grab the actual value.',
  },
  {
    question: 'What is the main advantage of INDEX/MATCH over VLOOKUP?',
    options: [
      'It is faster',
      'It uses less memory',
      'It can look in any direction, not just left to right',
      'It supports more data types',
    ],
    correctIndex: 2,
    explanation:
      'VLOOKUP can only return values from columns to the RIGHT of the search column. INDEX/MATCH can look in any direction because the lookup range and return range are independent.',
  },
  {
    question:
      '=INDEX(B1:B5, MATCH("offer_123", A1:A5, 0)) -- what does this return?',
    context: {
      headers: ['A: Offer ID', 'B: Clicks'],
      rows: [
        ['offer_101', '2,340'],
        ['offer_102', '1,890'],
        ['offer_123', '1,250'],
        ['offer_145', '3,100'],
        ['offer_167', '890'],
      ],
    },
    options: [
      'offer_123',
      '1,250',
      '3',
      '2,340',
    ],
    correctIndex: 1,
    explanation:
      'MATCH("offer_123", A1:A5, 0) finds "offer_123" in position 3. INDEX(B1:B5, 3) then returns the 3rd value in the B column range, which is 1,250.',
  },
  {
    question: 'What does the 0 in MATCH("offer_123", A2:A6, 0) mean?',
    options: [
      'Start searching from row 0',
      'Return 0 if no match is found',
      'Exact match — find the value exactly as given',
      'Search from the bottom up',
    ],
    correctIndex: 2,
    explanation:
      'The third argument in MATCH is the match_type. 0 means exact match — the value must match precisely. This is similar to using FALSE in VLOOKUP.',
  },
  {
    question: 'If MATCH cannot find the search key, what happens?',
    options: [
      'It returns 0',
      'It returns -1',
      'It returns a #N/A error',
      'It returns the last position in the range',
    ],
    correctIndex: 2,
    explanation:
      'When MATCH cannot find the search key with exact match (0), it returns a #N/A error. You can wrap the formula in IFERROR to handle this gracefully.',
  },
  {
    question: 'Why doesn\'t INDEX/MATCH break when columns are inserted or moved?',
    options: [
      'It uses column letters that auto-adjust',
      'The lookup range and return range are specified independently',
      'It locks columns in place',
      'Google Sheets automatically updates INDEX/MATCH formulas',
    ],
    correctIndex: 1,
    explanation:
      'In INDEX/MATCH, you specify the return range (e.g., C2:C10) separately from the search range (e.g., A2:A10). Even if a column is inserted between them, both range references update independently and the formula still works correctly.',
  },
  {
    question: 'What does =INDEX(A2:A6, 1) return?',
    options: [
      'The value in cell A1',
      'The first value in the range A2:A6 (which is A2)',
      'The number 1',
      'An error because INDEX needs MATCH',
    ],
    correctIndex: 1,
    explanation:
      'INDEX(A2:A6, 1) returns the value at position 1 in the range, which is the first cell: A2. INDEX does not require MATCH — you can use any number as the row argument.',
  },
  {
    question: 'You want to look up "offer_123" and return its Clicks. Which approach works with this column layout?',
    context: {
      headers: ['A: Clicks', 'B: Offer ID'],
      rows: [
        ['2,340', 'offer_101'],
        ['1,890', 'offer_102'],
        ['1,250', 'offer_123'],
      ],
    },
    options: [
      'VLOOKUP searching column B and returning column A',
      'INDEX/MATCH with MATCH on column B and INDEX on column A',
      'Both approaches work equally well',
      'Neither can do this -- you need to rearrange the columns',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP cannot look left -- it always returns from a column to the right of the search column. INDEX/MATCH can return from any column, so it can search column B and return from column A.',
  },
  {
    question: 'Given this setup, what does =INDEX(C2:C5, MATCH(E2, A2:A5, 0)) return?',
    context: {
      headers: ['A: Offer ID', 'B: Name', 'C: Clicks', '', 'E: Lookup'],
      rows: [
        ['offer_101', 'CreditCards', '2,340', '', 'offer_145'],
        ['offer_123', 'InsureMe', '1,250', '', ''],
        ['offer_145', 'TravelDeals', '3,100', '', ''],
        ['offer_167', 'SaveMore', '890', '', ''],
      ],
    },
    options: [
      '2,340',
      '1,250',
      '3,100',
      'offer_145',
    ],
    correctIndex: 2,
    explanation:
      'E2 contains "offer_145". MATCH finds it at position 3 in A2:A5. INDEX(C2:C5, 3) returns the 3rd value in the Clicks column, which is 3,100.',
  },
]

/* ─── Shared demo data ─── */
const DEMO_DATA = [
  { id: 'offer_101', name: 'CreditCards Plus', clicks: 2340 },
  { id: 'offer_102', name: 'HomeLoans Direct', clicks: 1890 },
  { id: 'offer_123', name: 'InsureMe Now', clicks: 1250 },
  { id: 'offer_145', name: 'TravelDeals Pro', clicks: 3100 },
  { id: 'offer_167', name: 'SaveMore Bank', clicks: 890 },
]

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── MATCH Animation ─── */
function MatchAnimation() {
  const [step, setStep] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const matchIndex = 2 // 0-based in DEMO_DATA

  const STEPS = [
    {
      label: 'The formula',
      description: '=MATCH("offer_123", A2:A6, 0) — Find the position of offer_123',
    },
    {
      label: 'Checking row 1...',
      description: `"offer_101" — not a match`,
      scanRow: 0,
    },
    {
      label: 'Checking row 2...',
      description: `"offer_102" — not a match`,
      scanRow: 1,
    },
    {
      label: 'Match found!',
      description: `"offer_123" found at position 3 in the range`,
      scanRow: matchIndex,
      matched: true,
    },
    {
      label: 'Result: 3',
      description: 'MATCH returns 3 — "offer_123" is the 3rd item in A2:A6',
      scanRow: matchIndex,
      matched: true,
      showResult: true,
    },
  ]

  useEffect(() => {
    if (!isPlaying) return
    if (step >= STEPS.length - 1) {
      const t = setTimeout(() => setIsPlaying(false), 0)
      return () => clearTimeout(t)
    }
    const timer = setTimeout(() => setStep((s) => s + 1), 1500)
    return () => clearTimeout(timer)
  }, [step, isPlaying])

  const handlePlay = () => {
    setStep(0)
    setIsPlaying(true)
  }

  const currentStep = STEPS[step]

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlay}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
        >
          <Play className="w-3.5 h-3.5" />
          {step > 0 ? 'Replay' : 'Play'}
        </button>
        <button
          onClick={() => {
            setIsPlaying(false)
            setStep((s) => (s >= STEPS.length - 1 ? 0 : s + 1))
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <SkipForward className="w-3.5 h-3.5" />
          Step
        </button>
      </div>

      {/* Formula */}
      <div className="rounded-lg bg-gray-900 px-4 py-2">
        <code className="text-sm text-blue-400 font-mono">
          =MATCH("offer_123", A2:A6, 0)
        </code>
      </div>

      <AnimatePresence mode="wait">
        <motion.p
          key={step}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="text-sm text-blue-700 bg-blue-50 rounded-lg px-3 py-2 border border-blue-200"
        >
          <strong>{currentStep.label}</strong> {currentStep.description}
        </motion.p>
      </AnimatePresence>

      {/* Mini column */}
      <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm max-w-xs">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 w-8" />
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500 w-10">
                #
              </th>
              <th className="border-b border-gray-300 px-3 py-1.5 text-center text-xs text-gray-600 font-medium">
                A (Offer ID)
              </th>
            </tr>
          </thead>
          <tbody>
            {DEMO_DATA.map((row, i) => {
              const isScanning = currentStep.scanRow !== undefined && i <= currentStep.scanRow
              const isCurrent = currentStep.scanRow !== undefined && i === currentStep.scanRow
              const isMatch = isCurrent && currentStep.matched

              let cellClass = 'px-3 py-1.5 font-mono text-xs text-gray-800'
              if (isMatch)
                cellClass =
                  'px-3 py-1.5 font-mono text-xs bg-emerald-100 text-emerald-800 font-bold'
              else if (isCurrent)
                cellClass =
                  'px-3 py-1.5 font-mono text-xs bg-yellow-100 text-yellow-800'
              else if (isScanning)
                cellClass = 'px-3 py-1.5 font-mono text-xs text-gray-400'

              return (
                <tr key={i}>
                  <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-1.5">
                    {i + 2}
                  </td>
                  <td className="border-b border-r border-gray-200 text-center text-xs text-gray-400 py-1.5">
                    {i + 1}
                  </td>
                  <td className={`border-b border-gray-200 transition-colors duration-200 ${cellClass}`}>
                    <div className="flex items-center gap-1.5">
                      {isMatch && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 500 }}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        </motion.div>
                      )}
                      {row.id}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <AnimatePresence>
          {currentStep.showResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-blue-600 text-white text-center py-2 text-sm font-bold"
            >
              MATCH returns: 3
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ─── INDEX Animation ─── */
function IndexAnimation() {
  const [step, setStep] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const targetIdx = 2 // 0-based

  const STEPS = [
    {
      label: 'The formula',
      description: '=INDEX(B2:B6, 3) — Get the 3rd value in the Clicks column',
    },
    {
      label: 'Jump to position 3',
      description: 'INDEX goes directly to position 3 — no scanning needed!',
      highlight: targetIdx,
    },
    {
      label: 'Result: 1,250',
      description: 'The 3rd value in B2:B6 is 1,250',
      highlight: targetIdx,
      showResult: true,
    },
  ]

  useEffect(() => {
    if (!isPlaying) return
    if (step >= STEPS.length - 1) {
      const t = setTimeout(() => setIsPlaying(false), 0)
      return () => clearTimeout(t)
    }
    const timer = setTimeout(() => setStep((s) => s + 1), 1500)
    return () => clearTimeout(timer)
  }, [step, isPlaying])

  const handlePlay = () => {
    setStep(0)
    setIsPlaying(true)
  }

  const currentStep = STEPS[step]

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlay}
          className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700"
        >
          <Play className="w-3.5 h-3.5" />
          {step > 0 ? 'Replay' : 'Play'}
        </button>
        <button
          onClick={() => {
            setIsPlaying(false)
            setStep((s) => (s >= STEPS.length - 1 ? 0 : s + 1))
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <SkipForward className="w-3.5 h-3.5" />
          Step
        </button>
      </div>

      <div className="rounded-lg bg-gray-900 px-4 py-2">
        <code className="text-sm text-purple-400 font-mono">=INDEX(B2:B6, 3)</code>
      </div>

      <AnimatePresence mode="wait">
        <motion.p
          key={step}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="text-sm text-purple-700 bg-purple-50 rounded-lg px-3 py-2 border border-purple-200"
        >
          <strong>{currentStep.label}</strong> {currentStep.description}
        </motion.p>
      </AnimatePresence>

      <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm max-w-xs">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-2 py-1.5 text-center text-xs text-gray-500 w-8" />
              <th className="border-b border-r border-gray-300 px-3 py-1.5 text-center text-xs text-gray-500 w-10">
                #
              </th>
              <th className="border-b border-gray-300 px-3 py-1.5 text-center text-xs text-gray-600 font-medium">
                B (Clicks)
              </th>
            </tr>
          </thead>
          <tbody>
            {DEMO_DATA.map((row, i) => {
              const isHighlighted = currentStep.highlight === i

              let cellClass = 'px-3 py-1.5 text-gray-800 text-center'
              if (isHighlighted)
                cellClass =
                  'px-3 py-1.5 bg-purple-100 text-purple-900 font-bold text-center'

              return (
                <tr key={i}>
                  <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-1.5">
                    {i + 2}
                  </td>
                  <td className="border-b border-r border-gray-200 text-center text-xs text-gray-400 py-1.5">
                    {i + 1}
                  </td>
                  <td className={`border-b border-gray-200 transition-colors duration-200 ${cellClass}`}>
                    {isHighlighted && (
                      <motion.span
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                      >
                        {row.clicks.toLocaleString()}
                      </motion.span>
                    )}
                    {!isHighlighted && row.clicks.toLocaleString()}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <AnimatePresence>
          {currentStep.showResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-purple-600 text-white text-center py-2 text-sm font-bold"
            >
              INDEX returns: 1,250
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

/* ─── Combined INDEX/MATCH Animation ─── */
function CombinedAnimation() {
  const [step, setStep] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const STEPS = [
    {
      label: 'The combined formula',
      description:
        '=INDEX(B2:B6, MATCH("offer_123", A2:A6, 0))',
      phase: 'start',
    },
    {
      label: 'Step 1: MATCH runs first',
      description:
        'MATCH("offer_123", A2:A6, 0) searches column A for "offer_123"...',
      phase: 'match-search',
      scanRow: 0,
    },
    {
      label: 'MATCH scanning...',
      description: 'Checking each row in column A...',
      phase: 'match-search',
      scanRow: 1,
    },
    {
      label: 'MATCH found it!',
      description: '"offer_123" is at position 3 in the range',
      phase: 'match-found',
      matchRow: 2,
    },
    {
      label: 'Step 2: INDEX uses the result',
      description:
        'The formula becomes: =INDEX(B2:B6, 3) — get the 3rd value from column B',
      phase: 'index-jump',
      matchRow: 2,
    },
    {
      label: 'Result: 1,250!',
      description: 'INDEX returns the value at position 3 in B2:B6 = 1,250 clicks',
      phase: 'result',
      matchRow: 2,
    },
  ]

  useEffect(() => {
    if (!isPlaying) return
    if (step >= STEPS.length - 1) {
      const t = setTimeout(() => setIsPlaying(false), 0)
      return () => clearTimeout(t)
    }
    const timer = setTimeout(() => setStep((s) => s + 1), 2000)
    return () => clearTimeout(timer)
  }, [step, isPlaying])

  const handlePlay = () => {
    setStep(0)
    setIsPlaying(true)
  }

  const currentStep = STEPS[step]
  const phase = currentStep.phase

  // Formula display with dynamic highlighting
  function renderFormula() {
    if (phase === 'start') {
      return <span className="text-emerald-400">=INDEX(B2:B6, MATCH("offer_123", A2:A6, 0))</span>
    }

    if (phase === 'match-search' || phase === 'match-found') {
      return (
        <>
          <span className="text-gray-500">=INDEX(B2:B6, </span>
          <span className="text-blue-400 bg-blue-900/40 rounded px-1 font-bold">
            MATCH("offer_123", A2:A6, 0)
          </span>
          <span className="text-gray-500">)</span>
        </>
      )
    }

    if (phase === 'index-jump') {
      return (
        <>
          <span className="text-purple-400 bg-purple-900/40 rounded px-1 font-bold">
            =INDEX(B2:B6, 3)
          </span>
        </>
      )
    }

    // result
    return (
      <>
        <span className="text-gray-500">=INDEX(B2:B6, MATCH("offer_123", A2:A6, 0)) = </span>
        <span className="text-emerald-400 font-bold text-lg">1,250</span>
      </>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlay}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          <Play className="w-4 h-4" />
          {step > 0 ? 'Replay' : 'Play'}
        </button>
        <button
          onClick={() => {
            setIsPlaying(false)
            setStep((s) => (s >= STEPS.length - 1 ? 0 : s + 1))
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <SkipForward className="w-4 h-4" />
          Next Step
        </button>
        <span className="text-xs text-gray-400">
          Step {step + 1} of {STEPS.length}
        </span>
      </div>

      {/* Step progress */}
      <div className="flex gap-1">
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
              i <= step ? 'bg-emerald-500' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      {/* Formula bar */}
      <div className="rounded-lg bg-gray-900 p-4">
        <pre className="font-mono text-base">
          <AnimatePresence mode="wait">
            <motion.span
              key={phase}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderFormula()}
            </motion.span>
          </AnimatePresence>
        </pre>
      </div>

      {/* Step description */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          className={`rounded-lg border px-4 py-3 ${
            phase.startsWith('match')
              ? 'border-blue-200 bg-blue-50'
              : phase === 'index-jump'
              ? 'border-purple-200 bg-purple-50'
              : 'border-emerald-200 bg-emerald-50'
          }`}
        >
          <p
            className={`text-sm font-semibold ${
              phase.startsWith('match')
                ? 'text-blue-800'
                : phase === 'index-jump'
                ? 'text-purple-800'
                : 'text-emerald-800'
            }`}
          >
            {currentStep.label}
          </p>
          <p
            className={`text-sm ${
              phase.startsWith('match')
                ? 'text-blue-700'
                : phase === 'index-jump'
                ? 'text-purple-700'
                : 'text-emerald-700'
            }`}
          >
            {currentStep.description}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* Two-column table */}
      <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
        <AnimatePresence>
          {phase === 'result' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-emerald-500 text-white text-center py-2.5 text-sm font-bold flex items-center justify-center gap-2"
            >
              <CheckCircle2 className="w-5 h-5" />
              Result: 1,250 clicks
            </motion.div>
          )}
        </AnimatePresence>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-2 py-2 text-center text-xs text-gray-500 w-8" />
              <th
                className={`border-b border-r border-gray-300 px-3 py-2 text-center text-xs font-medium transition-colors duration-300 ${
                  phase.startsWith('match')
                    ? 'text-blue-700 bg-blue-50'
                    : 'text-gray-600'
                }`}
              >
                A (Offer ID)
              </th>
              <th
                className={`border-b border-gray-300 px-3 py-2 text-center text-xs font-medium transition-colors duration-300 ${
                  phase === 'index-jump' || phase === 'result'
                    ? 'text-purple-700 bg-purple-50'
                    : 'text-gray-600'
                }`}
              >
                B (Clicks)
              </th>
            </tr>
          </thead>
          <tbody>
            {DEMO_DATA.map((row, i) => {
              const isScanning =
                (phase === 'match-search') && currentStep.scanRow !== undefined && i <= currentStep.scanRow
              const isCurrent =
                (phase === 'match-search') && currentStep.scanRow !== undefined && i === currentStep.scanRow
              const isMatchRow =
                currentStep.matchRow !== undefined && i === currentStep.matchRow

              // Col A
              let colAClass = 'border-b border-r border-gray-200 px-3 py-2 font-mono text-xs text-gray-800'
              if (isMatchRow)
                colAClass =
                  'border-b border-r border-gray-200 px-3 py-2 font-mono text-xs bg-blue-100 text-blue-800 font-bold'
              else if (isCurrent)
                colAClass =
                  'border-b border-r border-gray-200 px-3 py-2 font-mono text-xs bg-yellow-100 text-yellow-800'
              else if (isScanning)
                colAClass =
                  'border-b border-r border-gray-200 px-3 py-2 font-mono text-xs text-gray-400'

              // Col B
              let colBClass = 'border-b border-gray-200 px-3 py-2 text-gray-800 text-center'
              if (
                isMatchRow &&
                (phase === 'index-jump' || phase === 'result')
              )
                colBClass =
                  'border-b border-gray-200 px-3 py-2 bg-purple-100 text-purple-900 font-bold text-center'

              return (
                <tr key={i}>
                  <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-2">
                    {i + 2}
                  </td>
                  <td className={`transition-colors duration-200 ${colAClass}`}>
                    <div className="flex items-center gap-1.5">
                      {isMatchRow && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 500 }}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 text-blue-600" />
                        </motion.div>
                      )}
                      {row.id}
                    </div>
                  </td>
                  <td className={`transition-colors duration-200 ${colBClass}`}>
                    {row.clicks.toLocaleString()}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ─── Side-by-side Comparison ─── */
function Comparison() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="rounded-xl border-2 border-green-200 bg-green-50 p-5"
      >
        <h4 className="font-bold text-green-800 mb-3 flex items-center gap-2">
          <span className="text-lg">VLOOKUP</span>
        </h4>
        <div className="rounded-lg bg-gray-900 px-3 py-2 mb-3">
          <code className="text-xs text-green-400 font-mono">
            =VLOOKUP("offer_123", A:C, 3, FALSE)
          </code>
        </div>
        <ul className="space-y-2 text-sm text-green-800">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            Simple, one function
          </li>
          <li className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
            Can only look RIGHT
          </li>
          <li className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
            Column index breaks if columns are inserted
          </li>
        </ul>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.15 }}
        className="rounded-xl border-2 border-blue-200 bg-blue-50 p-5"
      >
        <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
          <Zap className="w-5 h-5" />
          <span className="text-lg">INDEX/MATCH</span>
        </h4>
        <div className="rounded-lg bg-gray-900 px-3 py-2 mb-3">
          <code className="text-xs text-blue-400 font-mono">
            =INDEX(C2:C6, MATCH("offer_123", A2:A6, 0))
          </code>
        </div>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            Can look in ANY direction
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            Does not break when columns move
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            More flexible and reliable
          </li>
        </ul>
      </motion.div>
    </div>
  )
}

/* ─── Main Module ─── */
export default function Module6() {
  return (
    <ModuleLayout moduleId={6}>
      <div className="space-y-8">
        {/* Section 1: Why Not Just VLOOKUP? */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">
                Why Not Just VLOOKUP?
              </h2>
              <p className="text-lg text-gray-600">
                VLOOKUP works great, but it has two important limitations:
              </p>
            </div>

            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="flex items-start gap-4 rounded-xl border-2 border-red-200 bg-red-50 p-5"
              >
                <span className="text-3xl font-bold text-red-300">1</span>
                <div>
                  <h4 className="font-bold text-red-800">
                    It can only look RIGHT
                  </h4>
                  <p className="text-sm text-red-700 mt-1">
                    The search column must be the leftmost column in your range. If the data you
                    want is to the LEFT of the search column, VLOOKUP simply cannot do it.
                  </p>
                  <div className="mt-3 rounded-lg bg-white border border-red-200 p-3">
                    <p className="text-xs text-gray-500 mb-2">
                      Example: If Clicks is in column A and Offer ID is in column B, VLOOKUP
                      cannot look up by Offer ID and return Clicks (because Clicks is to the left).
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="bg-purple-100 text-purple-700 rounded px-2 py-0.5 font-mono">
                        A: Clicks
                      </span>
                      <span className="text-red-400 font-bold">&larr;</span>
                      <span className="bg-blue-100 text-blue-700 rounded px-2 py-0.5 font-mono">
                        B: Offer ID
                      </span>
                      <span className="text-red-500 text-xs">VLOOKUP cannot go left!</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 }}
                className="flex items-start gap-4 rounded-xl border-2 border-red-200 bg-red-50 p-5"
              >
                <span className="text-3xl font-bold text-red-300">2</span>
                <div>
                  <h4 className="font-bold text-red-800">
                    Column numbers break when columns move
                  </h4>
                  <p className="text-sm text-red-700 mt-1">
                    If you use <code className="bg-red-100 px-1 rounded">column_index = 3</code>{' '}
                    and someone inserts a new column, your formula now points at the wrong column.
                    VLOOKUP does not know the column moved.
                  </p>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center"
            >
              <p className="text-emerald-800 font-semibold text-lg">
                INDEX/MATCH is more flexible and more reliable.
              </p>
              <p className="text-sm text-emerald-600 mt-1">
                Let's break it into two simple parts.
              </p>
            </motion.div>
          </div>
        </Section>

        {/* Section 2: MATCH */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-bold text-blue-700">
                  Part 1
                </span>
                <h2 className="text-2xl font-bold text-gray-900">
                  MATCH — Finding the Position
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                MATCH tells you <strong>where</strong> something is — the row number within a
                range.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-base font-mono">
                <span className="text-gray-500">=</span>
                <span className="text-blue-400">MATCH</span>
                <span className="text-gray-500">(</span>
                <span className="text-amber-300">search_key</span>
                <span className="text-gray-500">, </span>
                <span className="text-green-300">range</span>
                <span className="text-gray-500">, </span>
                <span className="text-purple-300">match_type</span>
                <span className="text-gray-500">)</span>
              </code>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="bg-amber-900/40 text-amber-300 rounded px-2 py-0.5">
                  search_key = what to find
                </span>
                <span className="bg-green-900/40 text-green-300 rounded px-2 py-0.5">
                  range = where to look
                </span>
                <span className="bg-purple-900/40 text-purple-300 rounded px-2 py-0.5">
                  0 = exact match
                </span>
              </div>
            </div>

            <MatchAnimation />
          </div>
        </Section>

        {/* Section 3: INDEX */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="rounded-full bg-purple-100 px-3 py-1 text-sm font-bold text-purple-700">
                  Part 2
                </span>
                <h2 className="text-2xl font-bold text-gray-900">
                  INDEX — Fetching the Value
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                INDEX gets the value at a specific position in a range. No searching — it goes
                straight to the position you tell it.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-base font-mono">
                <span className="text-gray-500">=</span>
                <span className="text-purple-400">INDEX</span>
                <span className="text-gray-500">(</span>
                <span className="text-green-300">range</span>
                <span className="text-gray-500">, </span>
                <span className="text-amber-300">row_num</span>
                <span className="text-gray-500">)</span>
              </code>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="bg-green-900/40 text-green-300 rounded px-2 py-0.5">
                  range = the column to fetch from
                </span>
                <span className="bg-amber-900/40 text-amber-300 rounded px-2 py-0.5">
                  row_num = which position
                </span>
              </div>
            </div>

            <IndexAnimation />
          </div>
        </Section>

        {/* Section 4: Combining Them */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Combining Them</h2>
              <p className="text-lg text-gray-600">
                MATCH finds the position. INDEX uses that position to get the value.
                Together, they replace VLOOKUP — and do more.
              </p>
            </div>

            <div className="rounded-lg bg-gray-900 p-4">
              <code className="text-base font-mono">
                <span className="text-gray-500">=</span>
                <span className="text-purple-400">INDEX</span>
                <span className="text-gray-500">(B2:B6, </span>
                <span className="text-blue-400">MATCH</span>
                <span className="text-gray-500">("offer_123", A2:A6, 0)</span>
                <span className="text-gray-500">)</span>
              </code>
              <div className="mt-3 pt-3 border-t border-gray-700 text-sm text-gray-400">
                <span className="text-blue-400">MATCH</span> finds the position (3) &rarr;{' '}
                <span className="text-purple-400">INDEX</span> fetches the value at position 3 (1,250)
              </div>
            </div>

            <CombinedAnimation />

            <div className="border-t border-gray-200 pt-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-800">
                Side-by-side: VLOOKUP vs INDEX/MATCH
              </h3>
              <Comparison />
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="mt-4 text-center text-sm text-gray-500"
              >
                Same result, but INDEX/MATCH can look in <strong>any direction</strong> and does
                not break when columns move.
              </motion.p>
            </div>
          </div>
        </Section>

        {/* Section 5: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={6} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
